import Row from '@amzn/meridian/row';
import React, { useContext, useEffect, useState } from 'react';
import { StateContext } from '../../../GlobalStateStore';
import { EmptyTeamProfile, TeamProfile } from '../../../Types/TeamProfile';
import DataState from '../../../Types/DataState';
import { useParams } from 'react-router';
import DataSource from '../../../api';
import TeamPermissions from '../TeamPermissions';
import CoachingNote from '../../../Types/CoachingNote';
import {
  AssociateCardSummary,
  EmptyHMDGoal,
  EmptyTeam,
  Metric,
  Team as TeamType,
  WeeklySummaryCoachingStatus,
} from '../../../Types';
import ProfileHeader from './ProfileHeader';
import Loader from '@amzn/meridian/loader';
import AssociateCardsContainer from './AssociateCardContainer';
import Column from '@amzn/meridian/column';
import Alert from '@amzn/meridian/alert';
import { FormattedMessage } from 'react-intl';
import { TrendAndDetailsMetrics } from '../Table/TimeTrends/types';
import {
  Goal,
  GoalStatus,
  PresetDate,
  TimeOption,
  getMonthDateRange,
} from '@amzn/aet-galactus-kit';
import moment from 'moment';
import {
  formatPerformanceSummary,
  formatMonthMetrics,
  filterMetrics,
} from './Util/team';
import { Feature, isFeatureEnabled } from '../../../config/FeaturesConfig';
import {
  LighthouseFilters,
  TRAILING_DAYS,
} from '../../Dashboard/DashboardComponents/LighthouseMetrics/types';
import {
  EmptyFilters,
  getTeam,
  getTeamIsTM,
  isTmCheck,
} from '../../Dashboard/DashboardComponents/LighthouseMetrics/helper';
import { AssociateInsight } from '../../../Types/Team';
import { EPOCH_START } from '../../../Util/GlobalConstants';
import { pastWeekDates, formatMomentDates } from '../../../Util/DateHelper';
import { sortVerticalsAndPutTotalFirst } from './Util/helper';
import { clone } from 'lodash';

const ARCTeam: React.FC = (): React.ReactElement => {
  const apiNaws = DataSource.getDataFeed('NAWS');

  // Common Setting
  let { userId } = useParams() as { userId: string };
  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;

  const { userID, cscEntityPrefix, requesterOwner } = inspireOptions.data;
  userId = userId || userID;

  // Team Profile Related
  const [teamProfile, setTeamProfile] = useState<TeamProfile>(EmptyTeamProfile);
  const [teamProfileState, setTeamProfileState] = useState(DataState.LOADING);

  const [team, setTeam] = useState<TeamType>(EmptyTeam);
  const [teamState, setTeamState] = useState(DataState.LOADING);

  // Filter Related
  const [lighthouseFilters, setLighthouseFilters] =
    useState<LighthouseFilters>(EmptyFilters);
  const [lighthouseFiltersState, setLighthouseFiltersState] = useState(
    DataState.LOADING
  );
  const [defaultVertical, setDefaultVertical] = useState('');
  const [defaultVerticalState, setDefaultVerticalState] = useState(
    DataState.LOADING
  );
  const [verticalOptions, setVerticalOptions] = useState<string[]>([]);
  const [filterVerticalValue, setFilterVerticalValue] = useState('');

  const [teamDetailsMetrics, setTeamDetailsMetrics] = useState<
    TrendAndDetailsMetrics[]
  >([]);
  const [teamDetailsMetricsState, setTeamDetailsMetricsState] = useState(
    DataState.LOADING
  );
  const [lastMonthTeamDetailsMetrics, setLastMonthTeamDetailsMetrics] =
    useState<TrendAndDetailsMetrics[]>([]);
  const [
    lastMonthTeamDetailsMetricsState,
    setLastMonthTeamDetailsMetricsState,
  ] = useState(DataState.LOADING);

  const [weeklySummaryCoachingStatus, setWeeklySummaryCoachingStatus] =
    useState<WeeklySummaryCoachingStatus[]>([]);

  const [
    weeklySummaryCoachingStatusState,
    setWeeklySummaryCoachingStatusState,
  ] = useState(DataState.LOADING);

  const [associateLevelGoals, setAssociateLevelGoals] = useState<Goal[]>([]);
  const [associateLevelGoalsState, setAssociateLevelGoalsState] = useState(
    DataState.LOADING
  );

  const [associateInsights, setAssociateInsights] = useState<
    AssociateInsight[]
  >([]);
  const [associateInsightsState, setAssociateInsightsState] = useState(
    DataState.LOADING
  );

  const [associateCardSummary, setAssociateCardSummary] = useState<
    AssociateCardSummary[]
  >([]);

  const updateTeamWithProfile = (profile: TeamProfile) => {
    const availableKeys: string[] = [];
    if (inspireOptions.data.manageCoachingNotes) {
      availableKeys.push(TeamPermissions.coachingNotes);
    }

    const teamCopy = new TeamType({
      keys: availableKeys,
      metrics: [],
      summaryMetrics: [],
    });

    profile.reportingTeam.forEach(member => {
      const coachingNote = new CoachingNote(member.relatedUser.userID);
      const metric = new Metric({
        coachingNotes: [coachingNote],
        hmdGoal: EmptyHMDGoal,
      });
      teamCopy.metrics.push({
        user: member.relatedUser,
        metrics: metric,
        tm: member.isManager,
      });
    });
    setTeam(teamCopy);
  };

  useEffect(() => {
    if (inspireOptions.state !== DataState.LOADED) {
      return;
    }

    if (userId) {
      const getTeamProfileDataQuery = apiNaws.prepareTeamProfileDataQuery(
        userId,
        cscEntityPrefix
      );
      apiNaws.getTeamProfileData(
        getTeamProfileDataQuery,
        (prof: TeamProfile) => {
          updateTeamWithProfile(prof);
          setTeamProfile(prof);
        },
        (state: any) => {
          setTeamState(state);
          setTeamProfileState(state);
        }
      );

      apiNaws.getWeeklySummaryCoachingStatus(
        { userID: `${userId}` },
        setWeeklySummaryCoachingStatus,
        setWeeklySummaryCoachingStatusState
      );
    }
  }, [inspireOptions.state]);

  useEffect(() => {
    if (
      teamProfile.user.userID === '' ||
      teamProfileState === DataState.LOADING
    ) {
      return;
    }

    const isProfileTm = isTmCheck(teamProfile);
    // Initialize filter Options to Avoid Race Condition
    if (lighthouseFilters !== EmptyFilters) {
      setLighthouseFilters(EmptyFilters);
      setLighthouseFiltersState(DataState.LOADING);
    }

    // trailing 7 days or weekly
    const queryVariablesFilters = apiNaws.prepareLighthouseFiltersQuery(
      teamProfile.user.userID,
      cscEntityPrefix,
      isProfileTm,
      TRAILING_DAYS,
      TimeOption.DAILY,
      getTeam(teamProfile),
      getTeamIsTM(teamProfile),
      false
    );

    apiNaws.getLighthouseFilters(
      queryVariablesFilters,
      setLighthouseFilters,
      setLighthouseFiltersState
    );
  }, [teamProfile, teamProfileState]);

  useEffect(() => {
    if (
      teamProfile.user.userID === '' ||
      teamProfileState === DataState.LOADING
    ) {
      return;
    }
    const queryVariables = apiNaws.prepareLighthouseDefaultVerticalQuery(
      teamProfile.user.userID,
      cscEntityPrefix
    );

    apiNaws.getDefaultVertical(
      queryVariables,
      setDefaultVertical,
      setDefaultVerticalState
    );
  }, [teamProfileState]);

  useEffect(() => {
    if (
      lighthouseFiltersState !== DataState.LOADED ||
      defaultVerticalState !== DataState.LOADED
    ) {
      return;
    }
    const sortedVerticals = sortVerticalsAndPutTotalFirst(
      lighthouseFilters.verticals
    );
    if (filterVerticalValue === '') {
      defaultVertical === null
        ? setFilterVerticalValue(sortedVerticals[0])
        : setFilterVerticalValue(defaultVertical);
    }
    setVerticalOptions(sortedVerticals);
  }, [lighthouseFiltersState, defaultVerticalState]);

  useEffect(() => {
    if (
      userId &&
      teamProfileState === DataState.LOADED &&
      associateInsightsState === DataState.LOADED &&
      associateLevelGoalsState === DataState.LOADED &&
      teamDetailsMetricsState === DataState.LOADED &&
      lastMonthTeamDetailsMetricsState === DataState.LOADED
    ) {
      let associateCardSummaryData: AssociateCardSummary[] = [];
      //@ts-ignore
      teamProfile.reportingTeam.map(member => {
        associateCardSummaryData.push({
          userID: member.relatedUser.userID,
          isManager: member.isManager,
          userAlias: member.relatedUser.alias,
          relationToUser: member.relatedUser.relationToUser,
          coachings: {
            lastCoachingStatus:
              weeklySummaryCoachingStatus
                .filter(
                  weeklySummary =>
                    weeklySummary.userID === member.relatedUser.userID
                )
                .map(data => data.lastCoachingStatus)[0] ?? '',
            lastCoachedDate:
              weeklySummaryCoachingStatus
                .filter(
                  weeklySummary =>
                    weeklySummary.userID === member.relatedUser.userID
                )
                .map(data => data.lastCoachedDate)[0] ?? '',
          },
          opportunities: {
            labelAndCount: associateInsights
              .filter(associateInsight =>
                associateInsight.associate.includes(
                  `${cscEntityPrefix}:${member.relatedUser.userID}`
                )
              )
              .flatMap(associateInsight =>
                associateInsight.opportunities
                  .filter(
                    opp =>
                      !isFeatureEnabled(Feature.ENABLE_CCX_REMOVAL) ||
                      !opp.category.toUpperCase().includes('CCX')
                  )
                  .map(opp => ({
                    category: opp.category,
                    count: opp.contacts.length,
                    contacts: clone(opp.contacts).sort((contactA, contactB) =>
                      contactA.contact.localeCompare(contactB.contact)
                    ),
                  }))
              ),
          },
          kudos: {
            labelAndCount: associateInsights
              .filter(associateInsight =>
                associateInsight.associate.includes(
                  `${cscEntityPrefix}:${member.relatedUser.userID}`
                )
              )
              .flatMap(associateInsight =>
                associateInsight.kudos
                  .filter(
                    kudo =>
                      !isFeatureEnabled(Feature.ENABLE_CCX_REMOVAL) ||
                      !kudo.category.toUpperCase().includes('CCX')
                  )
                  .map(kudo => ({
                    category: kudo.category,
                    count: kudo.contacts.length,
                    contacts: clone(kudo.contacts).sort((contactA, contactB) =>
                      contactA.contact.localeCompare(contactB.contact)
                    ),
                  }))
              ),
          },
          goals: clone(
            associateLevelGoals.filter(
              goal =>
                goal.userID.includes(member.relatedUser.userID) &&
                goal.status === GoalStatus.Open
            )
          ).sort(
            (a, b) =>
              new Date(a.endDate).getTime() - new Date(b.endDate).getTime()
          ),
          performance: {
            trendSummary:
              teamDetailsMetrics
                .filter(teamDetailsMetric =>
                  teamDetailsMetric.userID.includes(member.relatedUser.userID)
                )
                .flatMap(metrics =>
                  metrics?.metricDatas?.trend?.flatMap(metricData => {
                    return formatPerformanceSummary(metricData);
                  })
                ) || [],
            metricTimestamp:
              clone(
                teamDetailsMetrics.filter(teamDetailsMetric =>
                  teamDetailsMetric.userID.includes(member.relatedUser.userID)
                )
              )
                .sort(
                  (
                    metricA: TrendAndDetailsMetrics,
                    metricB: TrendAndDetailsMetrics
                  ) =>
                    new Date(metricB.latestMetricPublishDate).getTime() -
                    new Date(metricA.latestMetricPublishDate).getTime()
                )
                .at(0)?.latestMetricPublishDate || EPOCH_START,
          },
          bottomQuartileMetrics: {
            metricSummary: associateInsights
              .filter(associateInsight =>
                associateInsight.associate.includes(
                  `${cscEntityPrefix}:${member.relatedUser.userID}`
                )
              )
              .flatMap(associateInsight =>
                associateInsight.bottomQuartileMetrics.map(metric => ({
                  metric: metric.metricName,
                  label: metric.label,
                }))
              ),
          },
          metricsFromPreviousMonth:
            lastMonthTeamDetailsMetrics
              .filter(metricData =>
                metricData.userID.includes(member.relatedUser.userID)
              )
              .flatMap(metrics =>
                metrics?.metricDatas?.trend?.flatMap(
                  metricData => formatMonthMetrics(metricData).slice(-1)[0]
                )
              )
              .filter(
                (
                  formattedMeteric
                ): formattedMeteric is {
                  metric: string;
                  value: string;
                  netAvg: string;
                } =>
                  formattedMeteric !== undefined &&
                  formattedMeteric.value !== undefined &&
                  formattedMeteric.netAvg !== undefined &&
                  typeof formattedMeteric.metric === 'string' &&
                  typeof formattedMeteric.value === 'string' &&
                  typeof formattedMeteric.netAvg === 'string' &&
                  associateInsights
                    .filter(associateInsight =>
                      associateInsight.associate.includes(
                        member.relatedUser.userID
                      )
                    )
                    .some(associate =>
                      associate.bottomQuartileMetrics.some(
                        metric => metric.metricName === formattedMeteric.metric
                      )
                    )
              ) || [],
        });
      });
      setAssociateCardSummary(associateCardSummaryData);
    }
  }, [
    userId,
    teamProfileState,
    weeklySummaryCoachingStatusState,
    associateInsightsState,
    teamDetailsMetricsState,
    associateLevelGoalsState,
    lastMonthTeamDetailsMetricsState,
  ]);

  // Edge Case Handle - DefaultVertical is null and FilterOptions is empty
  useEffect(() => {
    if (filterVerticalValue === undefined) {
      setTeamDetailsMetricsState(DataState.LOADED);
      setLastMonthTeamDetailsMetricsState(DataState.LOADED);
    }
  }, [filterVerticalValue]);

  useEffect(() => {
    if (
      userId &&
      teamProfileState === DataState.LOADED &&
      defaultVerticalState === DataState.LOADED &&
      lighthouseFiltersState === DataState.LOADED &&
      filterVerticalValue
    ) {
      // get current metrics
      const metricsDateRange = pastWeekDates();
      const customDate = [
        formatMomentDates(metricsDateRange.start),
        formatMomentDates(metricsDateRange.end),
      ];
      const queryTeamDetailsMetrics = apiNaws.prepareTeamDetailsMetricsQuery(
        userId,
        cscEntityPrefix,
        teamProfile.reportingTeam,
        filterVerticalValue,
        'ALL',
        'ALL',
        'ALL',
        'ALL',
        TimeOption.WEEKLY,
        customDate,
        filterMetrics(requesterOwner, filterVerticalValue),
        true
      );
      apiNaws.getTeamDetailsMetrics(
        queryTeamDetailsMetrics,
        setTeamDetailsMetrics,
        setTeamDetailsMetricsState
      );
      // get previous month metrics
      const { startDateOfMonth, endDateOfMonth } = getMonthDateRange(
        moment().clone().subtract(1, 'month').format('YYYY-MM-DD')
      );
      const customDateLastMonth = [startDateOfMonth, endDateOfMonth];

      const queryLastMonthTeamDetailsMetrics =
        apiNaws.prepareTeamDetailsMetricsQuery(
          userId,
          cscEntityPrefix,
          teamProfile.reportingTeam,
          filterVerticalValue,
          'ALL',
          'ALL',
          'ALL',
          'ALL',
          PresetDate.MONTHLY,
          customDateLastMonth,
          filterMetrics(requesterOwner, filterVerticalValue)
        );
      apiNaws.getTeamDetailsMetrics(
        queryLastMonthTeamDetailsMetrics,
        setLastMonthTeamDetailsMetrics,
        setLastMonthTeamDetailsMetricsState
      );
    }
  }, [
    userId,
    teamProfile,
    filterVerticalValue,
    teamProfileState,
    defaultVerticalState,
    lighthouseFiltersState,
  ]);

  useEffect(() => {
    if (userId && teamProfileState === DataState.LOADED) {
      const associateInsightQueryVariable =
        apiNaws.prepareQueryForGetAssociateInsights(
          `${cscEntityPrefix}:${userId}`,
          teamProfile.reportingTeam.map(
            reportingTeam =>
              `${cscEntityPrefix}:${reportingTeam.relatedUser.userID}`
          ),
          teamProfile.reportingTeam.map(
            reportingTeam => reportingTeam.relatedUser.alias
          ),
          TimeOption.DAILY,
          moment().format('YYYY-MM-DD')
        );

      apiNaws.getAssociateInsights(
        associateInsightQueryVariable,
        setAssociateInsights,
        setAssociateInsightsState
      );
    }
  }, [userId, teamProfileState]);

  useEffect(() => {
    if (teamProfileState === DataState.LOADED && teamProfile.user.userID) {
      const queryGetAssociateLevelGoals =
        apiNaws.prepareQueryforGetAssociateLevelGoals(
          `${cscEntityPrefix}:${teamProfile.user.userID}`,
          teamProfile.reportingTeam.map(
            reportingTeam =>
              `${cscEntityPrefix}:${reportingTeam.relatedUser.userID}`
          )
        );

      apiNaws.getAssociateLevelGoals(
        queryGetAssociateLevelGoals,
        setAssociateLevelGoals,
        setAssociateLevelGoalsState
      );
    }
  }, [teamProfile, teamProfileState]);

  if (teamProfileState === DataState.LOADING) {
    return (
      <Row alignmentHorizontal='center'>
        <Loader />
      </Row>
    );
  }

  if (teamProfileState === DataState.ERROR) {
    return (
      <Alert type='error'>
        <FormattedMessage
          id='cs_perfportal_error_refresh_page'
          defaultMessage='Something went wrong. Please try refreshing the page. If the problem persists, please submit a SIM ticket for us to troubleshoot.'
        />
      </Alert>
    );
  }

  return (
    <Column>
      <ProfileHeader teamProfile={teamProfile} />

      <AssociateCardsContainer
        defaultVertical={filterVerticalValue}
        teamProfile={teamProfile}
        associateCardSummary={associateCardSummary}
        summaryState={weeklySummaryCoachingStatusState}
        metricState={teamDetailsMetricsState}
        insightState={associateInsightsState}
        goalState={associateLevelGoalsState}
        verticalOptions={verticalOptions}
        verticalValue={filterVerticalValue}
        setVerticalValue={setFilterVerticalValue}
      />
    </Column>
  );
};

export default ARCTeam;
